import React from 'react';

import {
  buttonBaseClasses,
  ClickAwayListener,
  Grow,
  ListSubheader,
  listSubheaderClasses,
  MenuItem,
  menuItemClasses,
  MenuList,
  Paper,
  Popper,
  styled,
} from '@mui/material';

import { CheckmarkIcon } from '../../../assets/icons';

export type DropdownProps = {
  items: { name: string; groupName?: string }[];
  open: boolean;
  name: string;
  labelledby: string;
  anchor: HTMLButtonElement | null;
  onClose: () => void;
  onSelect: (index: number) => void;
  activeItem: number | null;
  groupingFn: <T extends { name: string; groupName?: string }>(
    items: T[],
  ) => Record<string, (T & { index: number })[]>;
};

const DropdownMenu = ({
  open,
  items,
  anchor,
  onClose,
  onSelect,
  name,
  labelledby,
  activeItem,
  groupingFn,
}: DropdownProps) => (
  <QuickNavDropdownPopper
    open={open}
    anchorEl={anchor}
    role={undefined}
    placement="bottom-start"
    transition
    disablePortal
  >
    {({ TransitionProps }) => (
      <Grow
        {...TransitionProps}
        style={{
          transformOrigin: 'center top',
          width: '100%',
          inset: 'unset !important',
        }}
      >
        <QuickNavDropdown>
          <ClickAwayListener onClickAway={onClose}>
            <QuickNavDropdownMenuList
              autoFocusItem={activeItem === null || activeItem === 0}
              id={name}
              aria-labelledby={labelledby}
            >
              {Object.entries(groupingFn(items)).map(([groupName, group]) => [
                groupName ? (
                  <StyleListSubheader>{groupName}</StyleListSubheader>
                ) : null,
                group.map((item) => (
                  <QuickNavDropdownMenuItem
                    className={groupName ? 'nested' : ''}
                    disableRipple
                    key={item.name}
                    onClick={() => onSelect(item.index)}
                    isActive={item.index === activeItem}
                  >
                    {item.name}
                    {item.index === activeItem && (
                      <CheckmarkIcon
                        style={{ color: 'var(--color-light-red)' }}
                      />
                    )}
                  </QuickNavDropdownMenuItem>
                )),
              ])}
            </QuickNavDropdownMenuList>
          </ClickAwayListener>
        </QuickNavDropdown>
      </Grow>
    )}
  </QuickNavDropdownPopper>
);

export default DropdownMenu;

const QuickNavDropdownPopper = styled(Popper)({
  width: '100%',
  zIndex: 1,
});

const QuickNavDropdown = styled(Paper)({
  backgroundColor: 'var(--color-white)',
  boxShadow: 'unset', // hide the gap between button and menu due to rounding issues.
  border: '2px solid var(--color-light-red)',
  borderTop: 0,
  marginTop: '-2px',
});

const QuickNavDropdownMenuList = styled(MenuList)({
  paddingTop: 0,

  [`.${menuItemClasses.root}:first-of-type`]: {
    paddingTop: '2px',
  },
});

const StyleListSubheader = styled(ListSubheader)(({ theme }) => ({
  [`&.${listSubheaderClasses.root}`]: {
    ...theme.typography.body,
    lineHeight: '32px',
    color: 'var(--color-dark-coal)',
    fontWeight: theme.typography.fontWeightMedium,
    paddingLeft: '30px',
  },
}));

const QuickNavDropdownMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})((props: { isActive?: boolean }) => ({ theme }) => ({
  ...theme.typography.body,

  minHeight: '32px',
  paddingTop: '4.5px',
  paddingBottom: '4.5px',
  whiteSpace: 'normal',

  color: props.isActive ? 'var(--color-light-red)' : undefined,

  '&:hover, &:focus': {
    color: 'var(--color-light-red)',
    backgroundColor: 'transparent',
  },

  [`&.${menuItemClasses.focusVisible}`]: {
    color: 'var(--color-light-red)',

    '&:hover': {
      backgroundColor: 'var(--color-medium-light-grey)',
    },
  },

  [`&.${buttonBaseClasses.root}.${menuItemClasses.root}`]: {
    paddingLeft: '30px',
    paddingRight: '30px',

    '&.nested': {
      paddingLeft: '40px',
    },
  },

  svg: {
    marginLeft: '10px',
  },
}));
